import { useEffect, useState } from 'react';
import { Moon, Sun, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';

type Theme = 'light' | 'dark' | 'system';
const KEY = 'cosmos:theme';

function effectiveDark(t: Theme): boolean {
  if (t === 'dark') return true;
  if (t === 'light') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

const BAR = { light: '#ffffff', dark: '#0f172a' };

function applyTheme(t: Theme) {
  document.documentElement.classList.toggle('dark', effectiveDark(t));
  // Phone status bar (index.html has one theme-color per system scheme): a manual choice wins over both.
  document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]').forEach((m) => {
    const scheme = m.media.includes('dark') ? 'dark' : 'light';
    m.content = t === 'system' ? BAR[scheme] : BAR[t];
  });
}

function loadTheme(): Theme {
  const v = localStorage.getItem(KEY);
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(loadTheme);

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(KEY, theme);
  }, [theme]);

  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme('system');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  const cycle = () => setTheme(theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light');
  const Icon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor;
  const title = theme === 'light' ? 'Light mode (click for dark)'
              : theme === 'dark'  ? 'Dark mode (click for system)'
              :                     'System theme (click for light)';

  return (
    <Button variant="ghost" size="icon" onClick={cycle} title={title} aria-label="Toggle theme">
      <Icon className="h-4 w-4" />
    </Button>
  );
}
